/**
 * In-app pricing bundles (the marketed products). Price ids are env-driven
 * (set VITE_STRIPE_* to override, e.g. a test-mode build); defaults are the live
 * PROVENANCE365 bundle prices and must match the API's planCatalog / moduleCatalog
 * bundle price ids so checkout activates the right quota + pillars.
 */
const env = import.meta.env as Record<string, string | undefined>;

export type BillingInterval = 'monthly' | 'annual';
export type BundleKey = 'tech_health' | 'deal_ready' | 'full_stack';

export interface Bundle {
  key: BundleKey;
  /** Must match the API subscription `label` (planCatalog spec label). */
  label: string;
  monthlyPriceId: string;
  annualPriceId: string;
  /** Display dollars — monthly list, annual total. */
  monthly: number;
  annual: number;
  blurb: string;
  features: string[];
  highlighted?: boolean;
}

export const BUNDLES: Bundle[] = [
  {
    key: 'tech_health',
    label: 'Tech Health',
    monthlyPriceId: env.VITE_STRIPE_TECH_HEALTH_PRICE_ID || 'price_1U0fAEFSMm0giEAWjWd9UTTV',
    annualPriceId: env.VITE_STRIPE_TECH_HEALTH_ANNUAL_PRICE_ID || 'price_1U0fAMFSMm0giEAWKV28nh83',
    monthly: 299,
    annual: 3229,
    blurb: 'Ongoing technical health for one company',
    features: ['Technical DD', '3 repositories', 'Risk register', 'Weekly + on-demand scans'],
  },
  {
    key: 'deal_ready',
    label: 'Deal Ready',
    monthlyPriceId: env.VITE_STRIPE_DEAL_READY_PRICE_ID || 'price_1U0fAlFSMm0giEAWbl2icryF',
    annualPriceId: env.VITE_STRIPE_DEAL_READY_ANNUAL_PRICE_ID || 'price_1U0fAtFSMm0giEAWeBiSs3qk',
    monthly: 649,
    annual: 7009,
    blurb: 'Sell-side readiness before a raise or sale',
    features: ['Everything in Tech Health', 'Legal DD', 'Financial DD', '10 repositories'],
  },
  {
    key: 'full_stack',
    label: 'Full-Stack DD',
    monthlyPriceId: env.VITE_STRIPE_FULL_STACK_PRICE_ID || 'price_1U0fB1FSMm0giEAWlmIfg3G9',
    annualPriceId: env.VITE_STRIPE_FULL_STACK_ANNUAL_PRICE_ID || 'price_1U0fBCFSMm0giEAWTHxKV7zp',
    monthly: 999,
    annual: 10789,
    blurb: 'The complete diligence stack',
    features: ['Everything in Deal Ready', 'Commercial DD', 'Multiple targets & clients', 'Priority support'],
    highlighted: true,
  },
];

export const BUNDLE_BY_KEY = Object.fromEntries(BUNDLES.map((b) => [b.key, b])) as Record<BundleKey, Bundle>;

export function priceIdFor(bundle: Bundle, interval: BillingInterval): string {
  return interval === 'annual' ? bundle.annualPriceId : bundle.monthlyPriceId;
}

export function isBundleKey(v: string | null | undefined): v is BundleKey {
  return v === 'tech_health' || v === 'deal_ready' || v === 'full_stack';
}
